import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { AccountTree, Warning } from '@mui/icons-material';
import mermaid from 'mermaid';
import mermaidConfig from '../../config/mermaidConfig';
import logger from '../../utils/logger';

export const MIN_ZOOM = 10;
export const MAX_ZOOM = 5000;

const FIT_PADDING = 24;

// Mermaid lays out synchronously, so a very large diagram locks the tab for
// minutes with no way out. Past this size we ask first instead of freezing.
const HEAVY_DIAGRAM_CHARS = 400000;

const clampZoom = (value) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, value));

// Yield to the browser so the loading state can paint before a heavy render.
const yieldToBrowser = () =>
  new Promise((resolve) => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(resolve, { timeout: 100 });
    } else {
      setTimeout(resolve, 0);
    }
  });

/**
 * Pan/zoom canvas that renders a Mermaid diagram and exposes navigation
 * controls to its parent via ref.
 *
 * Pan is written straight to the DOM through requestAnimationFrame rather than
 * React state - dragging a large diagram would otherwise re-render the whole
 * page on every mousemove.
 */
const DiagramViewport = forwardRef(function DiagramViewport(
  {
    mermaidCode,
    theme = 'default',
    layout = 'default',
    busy = false,
    busyTitle,
    busySubtitle,
    emptyTitle = 'No Diagram Generated',
    emptySubtitle = 'Configure your settings and click "Generate Diagram" to create a visualization',
    onZoomChange,
    onRenderingChange,
    onMatchesChange
  },
  ref
) {
  const containerRef = useRef(null);
  const contentRef = useRef(null);

  const [rendering, setRendering] = useState(false);
  const [confirmedHeavy, setConfirmedHeavy] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [renderError, setRenderError] = useState(null);

  // Live transform state. Kept in a ref so drag updates never trigger a render.
  const transformRef = useRef({ x: 0, y: 0, zoom: 100 });
  const frameRef = useRef(null);
  const dragRef = useRef(null);
  const pinchRef = useRef(null);
  const elkRegistered = useRef(false);
  // Incremented per render attempt so a slow earlier render cannot overwrite
  // the DOM (or clear the overlay) after a newer one has taken over.
  const renderTokenRef = useRef(0);
  // Set while a diagram is waiting for its first successful fit.
  const pendingFitRef = useRef(false);
  const canPanRef = useRef(false);

  // Search state for "find a node".
  const [matches, setMatches] = useState([]);
  const [matchIndex, setMatchIndex] = useState(0);
  const searchQueryRef = useRef('');

  const applyTransform = useCallback(() => {
    frameRef.current = null;
    const el = contentRef.current;
    if (!el) return;
    const { x, y, zoom: z } = transformRef.current;
    el.style.transform = `translate(${x}px, ${y}px) scale(${z / 100})`;
  }, []);

  // Re-assert the transform after every render so a remount (React StrictMode
  // does this in development) never leaves the element untransformed.
  useLayoutEffect(() => {
    applyTransform();
  });

  const scheduleTransform = useCallback(() => {
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(applyTransform);
  }, [applyTransform]);

  // Commit a zoom value, keeping the React mirror in sync for the toolbar.
  const commitZoom = useCallback(
    (nextZoom) => {
      const clamped = clampZoom(nextZoom);
      transformRef.current.zoom = clamped;
      setZoom(clamped);
      scheduleTransform();
      return clamped;
    },
    [scheduleTransform]
  );

  useEffect(() => {
    if (onZoomChange) onZoomChange(zoom);
  }, [zoom, onZoomChange]);

  useEffect(() => {
    if (onRenderingChange) onRenderingChange(rendering);
  }, [rendering, onRenderingChange]);

  // Natural (unscaled) size of the rendered SVG.
  const getContentSize = useCallback(() => {
    const svg = contentRef.current?.querySelector('svg');
    if (!svg) return null;
    const width = parseFloat(svg.style.width) || svg.getBoundingClientRect().width / (transformRef.current.zoom / 100);
    const height = parseFloat(svg.style.height) || svg.getBoundingClientRect().height / (transformRef.current.zoom / 100);
    if (!width || !height) return null;
    return { width, height };
  }, []);

  const fitToScreen = useCallback(() => {
    const container = containerRef.current;
    const size = getContentSize();
    if (!container || !size) return false;

    const availableWidth = container.clientWidth - FIT_PADDING * 2;
    const availableHeight = container.clientHeight - FIT_PADDING * 2;
    // The container can legitimately have no size yet - rendered while hidden,
    // or before the first layout. Report failure so it can be retried once it
    // does, rather than leaving the diagram permanently unfitted.
    if (availableWidth <= 0 || availableHeight <= 0) return false;

    const scale = Math.min(availableWidth / size.width, availableHeight / size.height);
    const nextZoom = clampZoom(scale * 100);
    const actualScale = nextZoom / 100;

    transformRef.current.zoom = nextZoom;
    transformRef.current.x = (container.clientWidth - size.width * actualScale) / 2;
    transformRef.current.y = (container.clientHeight - size.height * actualScale) / 2;

    setZoom(nextZoom);
    applyTransform();
    pendingFitRef.current = false;
    return true;
  }, [getContentSize, applyTransform]);

  const resetView = useCallback(() => {
    transformRef.current = { x: 0, y: 0, zoom: 100 };
    setZoom(100);
    scheduleTransform();
  }, [scheduleTransform]);

  // Zoom while keeping the given viewport point visually anchored.
  const zoomAt = useCallback(
    (nextZoom, anchorX, anchorY) => {
      const container = containerRef.current;
      if (!container) return;

      const { x, y, zoom: current } = transformRef.current;
      const clamped = clampZoom(nextZoom);
      if (clamped === current) return;

      const currentScale = current / 100;
      const nextScale = clamped / 100;

      // Content-space coordinate currently under the anchor point.
      const contentX = (anchorX - x) / currentScale;
      const contentY = (anchorY - y) / currentScale;

      transformRef.current.x = anchorX - contentX * nextScale;
      transformRef.current.y = anchorY - contentY * nextScale;
      transformRef.current.zoom = clamped;

      setZoom(clamped);
      scheduleTransform();
    },
    [scheduleTransform]
  );

  // Zoom from the centre of the viewport - used by buttons and keyboard.
  const zoomByStep = useCallback(
    (delta) => {
      const container = containerRef.current;
      if (!container) {
        commitZoom(transformRef.current.zoom + delta);
        return;
      }
      zoomAt(transformRef.current.zoom + delta, container.clientWidth / 2, container.clientHeight / 2);
    },
    [zoomAt, commitZoom]
  );

  const panBy = useCallback(
    (dx, dy) => {
      transformRef.current.x += dx;
      transformRef.current.y += dy;
      scheduleTransform();
    },
    [scheduleTransform]
  );

  // --- Search ------------------------------------------------------------

  const clearHighlights = useCallback(() => {
    const root = contentRef.current;
    if (!root) return;
    root.querySelectorAll('.diagram-search-hit, .diagram-search-active').forEach((el) => {
      el.classList.remove('diagram-search-hit', 'diagram-search-active');
    });
  }, []);

  const centerOnElement = useCallback(
    (el) => {
      const container = containerRef.current;
      if (!container || !el) return;

      // Measuring depends on the DOM already showing the latest transform. A
      // pending rAF would make getBoundingClientRect report the previous
      // position, and two centre calls in one frame would then double-count
      // the delta and throw the node off screen - so flush first.
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        applyTransform();
      }

      const nodeRect = el.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const dx = containerRect.left + containerRect.width / 2 - (nodeRect.left + nodeRect.width / 2);
      const dy = containerRect.top + containerRect.height / 2 - (nodeRect.top + nodeRect.height / 2);

      transformRef.current.x += dx;
      transformRef.current.y += dy;
      scheduleTransform();
    },
    [applyTransform, scheduleTransform]
  );

  const focusMatch = useCallback(
    (index, list) => {
      const source = list || matches;
      if (!source.length) return;
      const safeIndex = ((index % source.length) + source.length) % source.length;

      source.forEach((el) => el.classList.remove('diagram-search-active'));
      const target = source[safeIndex];
      target.classList.add('diagram-search-active');

      setMatchIndex(safeIndex);
      centerOnElement(target);
    },
    [matches, centerOnElement]
  );

  const search = useCallback(
    (query) => {
      searchQueryRef.current = query;
      clearHighlights();

      const root = contentRef.current;
      const trimmed = (query || '').trim().toLowerCase();

      if (!root || !trimmed) {
        setMatches([]);
        setMatchIndex(0);
        if (onMatchesChange) onMatchesChange({ count: 0, index: 0 });
        return;
      }

      const nodes = Array.from(root.querySelectorAll('g.node, g.cluster'));
      const hits = nodes.filter((node) => (node.textContent || '').toLowerCase().includes(trimmed));

      hits.forEach((node) => node.classList.add('diagram-search-hit'));
      setMatches(hits);

      if (hits.length) {
        focusMatch(0, hits);
      } else {
        setMatchIndex(0);
      }
      if (onMatchesChange) onMatchesChange({ count: hits.length, index: hits.length ? 0 : 0 });
    },
    [clearHighlights, focusMatch, onMatchesChange]
  );

  useEffect(() => {
    if (onMatchesChange) {
      onMatchesChange({ count: matches.length, index: matchIndex });
    }
  }, [matches.length, matchIndex, onMatchesChange]);

  const nextMatch = useCallback(() => focusMatch(matchIndex + 1), [focusMatch, matchIndex]);
  const prevMatch = useCallback(() => focusMatch(matchIndex - 1), [focusMatch, matchIndex]);

  // --- Rendering ---------------------------------------------------------

  const renderDiagram = useCallback(async () => {
    const host = contentRef.current;
    if (!host || !mermaidCode) return;

    const token = ++renderTokenRef.current;
    const isStale = () => token !== renderTokenRef.current;

    setRendering(true);
    setRenderError(null);
    // The previous SVG is about to be replaced, so the matched nodes we are
    // holding are about to become detached. Drop them now: centring on a
    // detached node measures as all-zero and pans the view to nowhere.
    setMatches([]);
    setMatchIndex(0);

    // Let the overlay paint before we block the main thread.
    await new Promise((resolve) => setTimeout(resolve, 50));
    if (isStale()) return;
    await yieldToBrowser();
    if (isStale()) return;

    let processedCode = mermaidCode;

    if (layout === 'elk') {
      if (!elkRegistered.current) {
        try {
          const elkLayouts = await import('@mermaid-js/layout-elk');
          mermaid.registerLayoutLoaders(elkLayouts.default || elkLayouts);
          elkRegistered.current = true;
          logger.log('[ELK] Layout loader registered on-demand');
        } catch (e) {
          logger.error('[ELK] Failed to register layout:', e.message);
        }
      }
      if (mermaidCode.startsWith('flowchart')) {
        processedCode = `%%{init: {"flowchart": {"defaultRenderer": "elk"}}}%%\n${mermaidCode}`;
      }
    }

    try {
      await yieldToBrowser();
      if (isStale()) return;

      const { svg } = await mermaid.render(`mermaid-${Date.now()}`, processedCode);
      await yieldToBrowser();

      // A newer render started while Mermaid was working - discard this result
      // rather than painting a diagram the user has already moved on from.
      if (isStale()) return;

      host.innerHTML = svg;

      // Crop the SVG to its real content bounds. getBBox forces layout, so this
      // is valid as soon as the markup is in the document - doing it here rather
      // than in a rAF avoids a frame at the wrong size and, more importantly,
      // avoids losing the write if the callback fires while the ref is detached.
      const svgElement = host.querySelector('svg');
      if (svgElement) {
        try {
          const bbox = svgElement.getBBox();
          if (bbox.width > 0 && bbox.height > 0) {
            const padding = 20;
            svgElement.setAttribute(
              'viewBox',
              `${bbox.x - padding} ${bbox.y - padding} ${bbox.width + padding * 2} ${bbox.height + padding * 2}`
            );
            svgElement.removeAttribute('width');
            svgElement.removeAttribute('height');
            svgElement.style.width = `${bbox.width + padding * 2}px`;
            svgElement.style.height = `${bbox.height + padding * 2}px`;
            svgElement.style.maxWidth = 'none';
          }
        } catch (bboxError) {
          logger.warn('[SVG Auto-fit] Could not get bounding box:', bboxError);
        }
      }

      // A freshly generated diagram should always land readable.
      fitToScreen();
      if (searchQueryRef.current) search(searchQueryRef.current);
    } catch (error) {
      if (isStale()) return;
      logger.error('Failed to render Mermaid diagram:', error);
      host.innerHTML = '';
      setRenderError({ message: error.message, code: processedCode });
    } finally {
      if (!isStale()) setRendering(false);
    }
  }, [mermaidCode, layout, fitToScreen, search]);

  useEffect(() => {
    setConfirmedHeavy(false);

    // Drop the previous diagram straight away. Rendering may not happen at all
    // - it is skipped when the new diagram is too large to render safely - and
    // leaving the old SVG up would misrepresent it as the current result.
    if (contentRef.current) {
      contentRef.current.innerHTML = '';
    }
    setMatches([]);
    setMatchIndex(0);
    setRenderError(null);
    transformRef.current = { x: 0, y: 0, zoom: 100 };
    setZoom(100);
    pendingFitRef.current = Boolean(mermaidCode);
  }, [mermaidCode]);

  const isHeavy = Boolean(mermaidCode) && mermaidCode.length > HEAVY_DIAGRAM_CHARS;
  const blockedForSize = isHeavy && !confirmedHeavy;

  // Panning must be off whenever the viewport is showing UI instead of a
  // diagram. The overlays live inside this container, and grabbing the pointer
  // on pointerdown retargets the subsequent click to the container - so the
  // buttons inside them (Render anyway, Show Mermaid code) never fire.
  const canPan = Boolean(mermaidCode) && !blockedForSize && !renderError;
  canPanRef.current = canPan;

  // Initialise Mermaid whenever the theme changes, then (re-)render.
  useEffect(() => {
    mermaid.initialize({ ...mermaidConfig, theme });
    if (mermaidCode && !blockedForSize) {
      renderDiagram();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, layout, mermaidCode, blockedForSize]);

  // --- Pointer interaction ----------------------------------------------

  const handlePointerDown = (e) => {
    if (!canPan || e.button !== 0) return;
    const container = containerRef.current;
    // Capture lets the drag continue outside the container. If the browser
    // refuses it, dragging must still start.
    try {
      container.setPointerCapture(e.pointerId);
    } catch (err) {
      // Not capturable - the drag simply ends when the pointer leaves.
    }
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX - transformRef.current.x,
      startY: e.clientY - transformRef.current.y
    };
    container.style.cursor = 'grabbing';
  };

  const handlePointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    transformRef.current.x = e.clientX - drag.startX;
    transformRef.current.y = e.clientY - drag.startY;
    scheduleTransform();
  };

  const endDrag = (e) => {
    const container = containerRef.current;
    if (dragRef.current && container) {
      try {
        container.releasePointerCapture(dragRef.current.pointerId);
      } catch (err) {
        // Pointer already released.
      }
      container.style.cursor = canPan ? 'grab' : 'default';
    }
    dragRef.current = null;
  };

  // Wheel: Ctrl/Cmd + wheel zooms on the cursor, plain wheel pans.
  const handleWheel = useCallback(
    (e) => {
      if (!canPanRef.current) return;
      e.preventDefault();

      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();

      if (e.ctrlKey || e.metaKey) {
        // Proportional steps so zooming feels consistent at every scale.
        const factor = Math.exp(-e.deltaY * 0.002);
        zoomAt(transformRef.current.zoom * factor, e.clientX - rect.left, e.clientY - rect.top);
      } else {
        panBy(-e.deltaX, -e.deltaY);
      }
    },
    [zoomAt, panBy]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    // passive: false so preventDefault actually stops the page scrolling.
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // Touch: one finger pans, two fingers pinch-zoom.
  const getTouchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e) => {
    if (!canPan) return;
    if (e.touches.length === 2) {
      const rect = containerRef.current.getBoundingClientRect();
      pinchRef.current = {
        distance: getTouchDistance(e.touches),
        centerX: (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left,
        centerY: (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top
      };
      dragRef.current = null;
    } else if (e.touches.length === 1) {
      dragRef.current = {
        pointerId: 'touch',
        startX: e.touches[0].clientX - transformRef.current.x,
        startY: e.touches[0].clientY - transformRef.current.y
      };
    }
  };

  const handleTouchMove = (e) => {
    if (!canPan) return;

    if (e.touches.length === 2 && pinchRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const distance = getTouchDistance(e.touches);
      const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
      const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;

      const ratio = distance / pinchRef.current.distance;
      zoomAt(transformRef.current.zoom * ratio, centerX, centerY);
      panBy(centerX - pinchRef.current.centerX, centerY - pinchRef.current.centerY);

      pinchRef.current = { distance, centerX, centerY };
    } else if (e.touches.length === 1 && dragRef.current) {
      transformRef.current.x = e.touches[0].clientX - dragRef.current.startX;
      transformRef.current.y = e.touches[0].clientY - dragRef.current.startY;
      scheduleTransform();
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) pinchRef.current = null;
    if (e.touches.length === 0) dragRef.current = null;
  };

  // Keep the centre of the view fixed when the container resizes - collapsing
  // the options panel or entering fullscreen should widen the view around what
  // you were looking at, not shift it off to one side.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;

    let previous = { width: container.clientWidth, height: container.clientHeight };

    const observer = new ResizeObserver(() => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      if (width === previous.width && height === previous.height) return;

      const deltaX = width - previous.width;
      const deltaY = height - previous.height;
      previous = { width, height };

      // A diagram that could not be fitted yet (rendered while the container
      // had no size) gets its fit now rather than staying at 100%.
      if (pendingFitRef.current && fitToScreen()) return;

      transformRef.current.x += deltaX / 2;
      transformRef.current.y += deltaY / 2;

      scheduleTransform();
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [scheduleTransform, fitToScreen]);

  useEffect(() => () => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      zoomIn: () => zoomByStep(25),
      zoomOut: () => zoomByStep(-25),
      setZoom: (value) => {
        const container = containerRef.current;
        if (!container) return commitZoom(value);
        return zoomAt(value, container.clientWidth / 2, container.clientHeight / 2);
      },
      fitToScreen,
      resetView,
      panBy,
      search,
      nextMatch,
      prevMatch,
      getSvgElement: () => contentRef.current?.querySelector('svg') || null,
      getContentSize,
      getViewState: () => ({ ...transformRef.current }),
      getContainer: () => containerRef.current
    }),
    [zoomByStep, commitZoom, zoomAt, fitToScreen, resetView, panBy, search, nextMatch, prevMatch, getContentSize]
  );

  const showOverlay = busy || rendering;
  const overlayTitle = busyTitle || (rendering ? 'Rendering diagram...' : 'Generating diagram...');
  const overlaySubtitle = busySubtitle || 'This may take a few moments for large diagrams';

  const emptyState = useMemo(
    () => (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 3
        }}
      >
        <AccountTree sx={{ fontSize: 64, mb: 2, opacity: 0.5, color: 'text.secondary' }} />
        <Typography variant="h6" gutterBottom color="text.primary">
          {emptyTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {emptySubtitle}
        </Typography>
      </Box>
    ),
    [emptyTitle, emptySubtitle]
  );

  return (
    <Box
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      sx={{
        flex: 1,
        minHeight: 0,
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRadius: 1,
        backgroundColor: 'background.default',
        overflow: 'hidden',
        position: 'relative',
        cursor: canPan ? 'grab' : 'default',
        userSelect: 'none',
        touchAction: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none'
      }}
    >
      {!mermaidCode && !showOverlay && !renderError && emptyState}

      {blockedForSize && !showOverlay && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            gap: 1.5,
            px: 4,
            zIndex: 11,
            backgroundColor: 'background.default'
          }}
        >
          <Warning sx={{ fontSize: 56, color: 'warning.main', opacity: 0.8 }} />
          <Typography variant="h6">This diagram is very large</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
            {Math.round(mermaidCode.length / 1000).toLocaleString()}k characters. Rendering it will
            lock up the browser tab, possibly for several minutes. Selecting a smaller block of
            applications will give you something you can actually read.
          </Typography>
          <Button variant="outlined" color="warning" onClick={() => setConfirmedHeavy(true)} sx={{ mt: 1 }}>
            Render anyway
          </Button>
        </Box>
      )}

      {renderError && (
        <Box sx={{ position: 'absolute', inset: 0, overflow: 'auto', p: 3, textAlign: 'center' }}>
          <Typography color="error" variant="subtitle1" gutterBottom>
            Failed to render diagram
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {renderError.message}
          </Typography>
          <Box component="details" sx={{ mt: 2, textAlign: 'left' }}>
            <Box component="summary" sx={{ cursor: 'pointer', color: 'primary.main' }}>
              Show Mermaid code
            </Box>
            <Box
              component="pre"
              sx={{
                mt: 1,
                p: 2,
                maxHeight: 400,
                overflow: 'auto',
                fontSize: '0.75rem',
                bgcolor: 'background.paper',
                border: (t) => `1px solid ${t.palette.divider}`,
                borderRadius: 1
              }}
            >
              {renderError.code}
            </Box>
          </Box>
        </Box>
      )}

      {showOverlay && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: (t) =>
              t.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            zIndex: 10,
            backdropFilter: 'blur(4px)'
          }}
        >
          <CircularProgress size={48} sx={{ mb: 2 }} color="primary" />
          <Typography variant="body1" color="text.primary" fontWeight={500}>
            {overlayTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {overlaySubtitle}
          </Typography>
        </Box>
      )}

      <div
        ref={contentRef}
        className="mermaid-diagram"
        style={{
          transformOrigin: 'top left',
          display: 'inline-block',
          willChange: 'transform'
        }}
      />
    </Box>
  );
});

export default DiagramViewport;
